import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Navigation, Footer } from '../components/landing';
import { localeFromPath } from '../i18n/path-locale';

const TITLE_EN = 'Designed by a caregiver. For caregivers. — AidFone';
const TITLE_FR = 'Conçu par un aidant. Pour les aidants. — AidFone';

export const About: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const locale = localeFromPath(location.pathname);

  useEffect(() => {
    document.title = locale === 'fr' ? TITLE_FR : TITLE_EN;
    document.documentElement.lang = locale === 'fr' ? 'fr-CA' : 'en';
  }, [locale]);

  return (
    <div className="min-h-screen bg-brand-bg font-body">
      <Navigation />

      <main className="pt-24">
        <section className="py-16">
          <div className="max-w-[800px] mx-auto px-6">
            <h1 className="font-display text-[clamp(2rem,5vw,3rem)] font-semibold text-center mb-6 leading-tight text-brand-text">
              {t('pages.about.title')}
            </h1>
            <p className="text-center text-brand-text-light text-lg mb-12 max-w-[600px] mx-auto">
              {t('pages.about.intro')}
            </p>
          </div>
        </section>

        <section className="py-12 bg-brand-bg-warm">
          <div className="max-w-[800px] mx-auto px-6">
            <h2 className="font-display text-[clamp(1.6rem,4vw,2.2rem)] font-semibold mb-6 text-brand-text">
              {t('pages.about.storyTitle')}
            </h2>
            <p className="text-brand-text-light text-lg mb-5 leading-relaxed">
              <Trans i18nKey="pages.about.storyP1" />
            </p>
            <p className="text-brand-text-light text-lg mb-5 leading-relaxed">
              <Trans i18nKey="pages.about.storyP2" />
            </p>
            <p className="text-brand-text-light text-lg leading-relaxed">
              <Trans i18nKey="pages.about.storyP3" />
            </p>
            <p className="mt-8 text-brand-text-light text-sm italic">
              {t('pages.about.attribution')}
            </p>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-[1100px] mx-auto px-6">
            <h2 className="font-display text-[clamp(1.6rem,4vw,2.2rem)] font-semibold text-center mb-12 text-brand-text">
              {t('pages.about.pillarsTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {(['protection', 'peace', 'made'] as const).map((id) => (
                <div key={id} className="bg-white border border-brand-border rounded-xl p-6">
                  <h3 className="font-display text-xl font-semibold mb-3 text-brand-text">
                    {t(`pages.about.pillars.${id}.title`)}
                  </h3>
                  <p className="text-brand-text-light leading-relaxed">
                    {t(`pages.about.pillars.${id}.body`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 bg-brand-bg-warm">
          <div className="max-w-[700px] mx-auto px-6 text-center">
            <h2 className="font-display text-[clamp(1.4rem,4vw,2rem)] font-semibold mb-4 text-brand-text">
              {t('pages.about.ctaTitle')}
            </h2>
            <p className="text-brand-text-light mb-8 text-lg">
              {t('pages.about.ctaBody')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/configure"
                className="inline-flex items-center justify-center px-7 py-3.5 font-body text-base font-semibold no-underline rounded-md bg-brand-accent text-white shadow-[0_4px_14px_rgba(224,123,76,0.35)] hover:bg-[#c96a3f] transition-all"
              >
                {t('pages.about.ctaPrimary')}
              </Link>
              <Link
                to={locale === 'fr' ? '/fr/contact' : '/contact'}
                className="inline-flex items-center justify-center px-7 py-3.5 font-body text-base font-semibold no-underline rounded-md border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white transition-all"
              >
                {t('pages.about.ctaSecondary')}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
